import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { TableCell, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { VideoFolderPicker } from '@/components/shared/VideoFolderPicker';
import { Checkbox } from '@/components/ui/checkbox';
import { Copy, MoreHorizontal, Trash2, Eye, EyeOff } from 'lucide-react';
import { DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { WebhookData } from './types';
import { getPlatformName, getPlatformLogo, getStatusLabel } from './types';

interface WebhookTableRowProps {
  webhook: WebhookData;
  videos: Array<{ id: string; title: string }>;
  allWebhooks: WebhookData[];
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  onCopy: (url: string) => void;
  onDelete: (id: string) => void;
  onEdit: (id: string) => void;
  onVideoChange: (id: string, videoId: string) => void;
  onTokenChange: (id: string, token: string) => void;
  onSaveTracking: (id: string) => void;
}

export function WebhookTableRow({
  webhook,
  videos,
  allWebhooks,
  isSelected,
  onToggleSelect,
  onCopy,
  onDelete,
  onEdit,
  onVideoChange,
  onTokenChange,
  onSaveTracking
}: WebhookTableRowProps) {
  useLanguage();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const status = getStatusLabel(webhook.status);

  // Get videos already used by other webhooks of the same platform
  const usedVideoIds = allWebhooks
    .filter(w => w.id !== webhook.id && w.platform === webhook.platform && w.selectedVideoId)
    .map(w => w.selectedVideoId);

  // Filter available videos (exclude already used ones)
  const availableVideos = videos.filter(video => !usedVideoIds.includes(video.id));

  const handleDelete = async () => {
    await onDelete(webhook.id);
    setShowDeleteDialog(false);
  };

  const handleSaveTracking = () => {
    onSaveTracking(webhook.id);
    setIsSaved(true);

    // Reset saved state after 3 seconds
    setTimeout(() => {
      setIsSaved(false);
    }, 3000);
  };

  return (
    <>
    <TableRow key={webhook.id} className="h-16 hover:bg-muted/30">
      <TableCell>
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggleSelect(webhook.id)}
        />
      </TableCell>
      <TableCell className="font-medium">
        <img
          src={getPlatformLogo(webhook.platform)}
          alt={getPlatformName(webhook.platform)}
          className={`max-w-[80px] object-contain ${webhook.platform === 'braip' ? 'max-h-[32px]' : 'max-h-[40px]'}`}
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <span
            className={`text-sm truncate max-w-[250px] ${webhook.status === 'creation' ? 'text-gray-500' : ''}`}
            title={webhook.status !== 'creation' ? webhook.url : ''}
          >
            {webhook.status === 'creation' ? translateInterfaceText("Salve o tracking para liberar a URL") : webhook.url}
          </span>
          {webhook.status !== 'creation' && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 shrink-0"
              onClick={() => onCopy(webhook.url)}
            >
              <Copy className="h-4 w-4" />
            </Button>
          )}
        </div>
      </TableCell>
      <TableCell>
        <VideoFolderPicker
          value={webhook.selectedVideoId}
          videos={availableVideos.map((v) => ({ id: v.id, title: v.title }))}
          onChange={(value) => onVideoChange(webhook.id, value)}
          triggerClassName="flex h-10 min-w-[160px] items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-sm text-foreground hover:border-[#4B7BFF] transition-colors"
        />
      </TableCell>
      <TableCell>
        {webhook.status === 'creation' ? (
          <div className="relative min-w-[160px]">
            <Input
              placeholder={webhook.platform === 'payt' ? translateInterfaceText("Chave de integração Payt (integration_key)") : translateInterfaceText("Token da plataforma")}
              type={showToken ? 'text' : 'password'}
              value={webhook.platformToken || ''}
              onChange={(e) => onTokenChange(webhook.id, e.target.value)}
              className="rounded-md h-10 text-sm pr-9"
            />
            <button
              type="button"
              onClick={() => setShowToken(v => !v)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground focus:outline-none"
            >
              {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        ) : webhook.platformToken ? (
          <div className="relative min-w-[160px]">
            <Input
              readOnly
              type={showToken ? 'text' : 'password'}
              value={webhook.platformToken}
              className="rounded-md h-10 text-sm pr-9 bg-muted border-0"
            />
            <button
              type="button"
              onClick={() => setShowToken(v => !v)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground focus:outline-none"
            >
              {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        ) : null}
      </TableCell>
      <TableCell>
        <Badge
          variant="outline"
          className={`${status.className} whitespace-nowrap px-3 py-1 rounded-md font-semibold text-xs inline-flex items-center`}
        >
          {status.label}
        </Badge>
      </TableCell>
      <TableCell>
        <div className="flex items-center justify-start gap-2">
          {/* Show save button only during creation status */}
          {webhook.status === 'creation' && (
            <Button
              className="whitespace-nowrap disabled:opacity-40 rounded-lg px-4 py-2 font-semibold transition-all"
              style={{
                backgroundColor: isSaved
                  ? '#2563EB'
                  : webhook.selectedVideoId && webhook.platformToken
                    ? '#2563EB'
                    : 'hsl(var(--muted))',
                color: webhook.selectedVideoId && webhook.platformToken ? 'white' : 'hsl(var(--muted-foreground))'
              }}
              size="sm"
              onClick={handleSaveTracking}
              disabled={!webhook.selectedVideoId || !webhook.platformToken || isSaved}
            >
              {isSaved ? translateInterfaceText("Salvo") : translateInterfaceText("Salvar Tracking")}
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48 animate-in zoom-in-95 duration-100">
              <DropdownMenuItem
                onClick={() => setShowDeleteDialog(true)}
                className="text-red-600 cursor-pointer transition-all duration-150 ease-in-out data-[highlighted]:bg-red-600 data-[highlighted]:text-white"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {translateInterfaceText("Excluir")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>

    <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
      <AlertDialogContent className="!w-[40vh] md:w-[35vh] rounded-xl p-6">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-xl font-semibold text-foreground">
            {translateInterfaceText("Excluir Tracking?")}</AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-muted-foreground mt-2">
            {translateInterfaceText("Tem certeza que deseja excluir o tracking da plataforma")}{" "}{getPlatformName(webhook.platform)}{translateInterfaceText("? Esta ação não pode ser desfeita.")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="mt-6 flex justify-end gap-3">
          <AlertDialogCancel className="h-11 px-5 rounded-[10px] border-border text-muted-foreground hover:bg-muted hover:text-[#1E293B] transition-colors">
            {translateInterfaceText("Cancelar")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            className="h-11 px-5 rounded-[10px] bg-red-600 hover:bg-red-700 text-white font-semibold transition-colors"
          >
            {translateInterfaceText("Excluir")}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
